import { useState, useRef, useEffect } from "react"
import { useParams, useNavigate } from "react-router"
import MapGL from "@urbica/react-map-gl"
// @ts-ignore
import Draw from "@urbica/react-map-gl-draw"
import "mapbox-gl/dist/mapbox-gl.css"
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css"
// @ts-ignore
import StaticMode from "@mapbox/mapbox-gl-draw-static-mode"
import bbox from "@turf/bbox"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import BirddogLayout from "../components/BirddogLayout"
import { fetchSale, fetchUserBidForSale } from "../services/salesApi"
import { Sale, Bid } from "../types"
import {
  getBadgeBaseClasses,
  getDraftBadgeClasses,
  getBiddingStatusBadgeClassName,
  getBiddingStatusLabel,
} from "../utils/badgeStyles"
import {
  useIsBirddogForester,
  useIsBirddogTimberBuyer,
} from "../hooks/useBirddogProfile"
import { MAPBOX_TOKEN, mapStyles } from "../../../shared/constants"
import mapVisualizationDrawStyles from "../../../shared/mapVisualizationDrawStyles"

export const SaleDetail = () => {
  const { saleId } = useParams<{ saleId: string }>()
  const navigate = useNavigate()
  const mapRef = useRef<MapGL>(null)

  const [sale, setSale] = useState<Sale | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userBid, setUserBid] = useState<Bid | null>(null)

  // Get user role
  const isForester = useIsBirddogForester()
  const isTimberBuyer = useIsBirddogTimberBuyer()

  const bidCount = sale?.bid_count || 0

  // Fetch sale data from API
  useEffect(() => {
    const loadSale = async () => {
      if (!saleId) {
        setError("Sale ID is required")
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        const saleData = await fetchSale(Number(saleId))
        setSale(saleData)
      } catch (err) {
        if (err instanceof Error) {
          // Check if it's a 404 error for more specific messaging
          if (err.message === "Sale not found") {
            setError("Sale not found")
          } else {
            setError(`Failed to load sale: ${err.message}`)
          }
        } else {
          setError("An unexpected error occurred")
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadSale()
  }, [saleId])

  // Fetch user's bid if they are a timber buyer
  useEffect(() => {
    const loadUserBid = async () => {
      if (!saleId || !isTimberBuyer) return

      try {
        const bidData = await fetchUserBidForSale(Number(saleId))
        setUserBid(bidData)
      } catch (err) {
        console.error("Failed to fetch user bid:", err)
        // Don't set error state for bid fetching, just log it
      }
    }

    loadUserBid()
  }, [saleId, isTimberBuyer])

  // Update viewport when sale data becomes available
  useEffect(() => {
    if (sale?.boundary_geom) {
      const bounds = bbox(sale.boundary_geom)
      const [minLng, minLat, maxLng, maxLat] = bounds

      setViewport({
        latitude: (minLat + maxLat) / 2,
        longitude: (minLng + maxLng) / 2,
        zoom: 12,
      })
    }
  }, [sale])

  // Determine bidding window status
  const getBiddingWindowStatus = () => {
    if (!sale) return "closed"

    const now = new Date()
    if (now < sale.bid_start_datetime) return "future"
    if (now >= sale.bid_start_datetime && now < sale.bid_stop_datetime)
      return "open"
    return "closed"
  }

  // Format datetime for display
  const formatDateTime = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    })
  }

  const biddingStatus = getBiddingWindowStatus()

  // Format currency helper function
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // Calculate initial viewport from boundary geometry
  const getInitialViewport = () => {
    if (!sale?.boundary_geom)
      return { latitude: 39.8283, longitude: -98.5795, zoom: 4 }

    const bounds = bbox(sale.boundary_geom)
    const [minLng, minLat, maxLng, maxLat] = bounds

    return {
      latitude: (minLat + maxLat) / 2,
      longitude: (minLng + maxLng) / 2,
      zoom: 12,
    }
  }

  const [viewport, setViewport] = useState(getInitialViewport())

  // Handle loading state
  if (isLoading) {
    return (
      <BirddogLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading sale details...</p>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  // Handle error state
  if (error) {
    return (
      <BirddogLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">
              {error === "Sale not found" ? "Sale Not Found" : "Error"}
            </h1>
            <p className="text-gray-600 mt-2">
              {error === "Sale not found"
                ? "The requested sale could not be found."
                : error}
            </p>
            <button
              onClick={() => void navigate("/birddog")}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md transition-colors"
            >
              Back to Sales
            </button>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  // Handle case where sale is still null after loading
  if (!sale) {
    return (
      <BirddogLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Sale Not Found</h1>
            <p className="text-gray-600 mt-2">
              The requested sale could not be found.
            </p>
            <button
              onClick={() => void navigate("/birddog")}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md transition-colors"
            >
              Back to Sales
            </button>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  // Convert boundary to GeoJSON FeatureCollection for Draw component
  const boundaryFeatureCollection = {
    type: "FeatureCollection" as const,
    features: [
      {
        type: "Feature" as const,
        geometry: sale.boundary_geom,
        properties: {
          name: sale.name,
        },
      },
    ],
  }

  return (
    <BirddogLayout>
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{sale.name}</h1>
              <p className="text-gray-600">{sale.location_text}</p>
            </div>
            <div className="text-right space-y-2">
              {/* Edit Button - Only available for foresters */}
              {isForester && (
                <div>
                  <button
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md transition-colors"
                    onClick={() =>
                      void navigate(`/birddog/sale/${saleId}/edit`)
                    }
                  >
                    Edit Sale
                  </button>
                </div>
              )}

              {/* Status and Bidding Information */}
              <div>
                {sale.status === "draft" ? (
                  <p className="text-sm text-gray-600">
                    <span
                      className={`${getBadgeBaseClasses()} ${getDraftBadgeClasses()}`}
                    >
                      Draft
                    </span>
                    <span className="block text-xs text-gray-500 mt-1">
                      Not visible to bidders
                    </span>
                  </p>
                ) : biddingStatus === "open" && isTimberBuyer ? (
                  <div>
                    <button
                      className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-md transition-colors mb-2"
                      onClick={() => {
                        if (userBid) {
                          alert("Edit bid functionality doesn't exist yet")
                        } else {
                          navigate(`/birddog/sale/${saleId}/bid`)
                        }
                      }}
                    >
                      {userBid ? "Edit Bid" : "Place Bid"}
                    </button>
                    <p className="text-sm text-gray-600">
                      Bidding closes at {formatDateTime(sale.bid_stop_datetime)}
                    </p>
                  </div>
                ) : biddingStatus === "future" && isTimberBuyer ? (
                  <div>
                    <span
                      className={`${getBadgeBaseClasses()} bg-blue-100 text-blue-800 border-blue-200`}
                    >
                      Active - Bidding Opens Soon
                    </span>
                    <p className="text-sm text-gray-600 mt-1">
                      Opens {formatDateTime(sale.bid_start_datetime)}
                    </p>
                  </div>
                ) : biddingStatus === "closed" && isTimberBuyer ? (
                  <div>
                    <span
                      className={`${getBadgeBaseClasses()} bg-gray-100 text-gray-800 border-gray-200`}
                    >
                      Bidding Closed
                    </span>
                    <p className="text-sm text-gray-600 mt-1">
                      Closed {formatDateTime(sale.bid_stop_datetime)}
                    </p>
                  </div>
                ) : !isTimberBuyer && !isForester ? (
                  <div>
                    <span
                      className={getBiddingStatusBadgeClassName(biddingStatus)}
                    >
                      {getBiddingStatusLabel(biddingStatus)}
                    </span>
                    <p className="text-sm text-gray-600 mt-1">
                      {biddingStatus === "open"
                        ? `Closes ${formatDateTime(sale.bid_stop_datetime)}`
                        : biddingStatus === "future"
                          ? `Opens ${formatDateTime(sale.bid_start_datetime)}`
                          : `Closed ${formatDateTime(sale.bid_stop_datetime)}`}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div>
        {/* Map and Summary Section */}
        <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-6 mb-8">
          {/* Map */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="h-64 md:h-96 lg:h-[500px]">
              <MapGL
                ref={mapRef}
                style={{ width: "100%", height: "100%" }}
                mapStyle={mapStyles.aerial.url}
                accessToken={MAPBOX_TOKEN}
                latitude={viewport.latitude}
                longitude={viewport.longitude}
                zoom={viewport.zoom}
                onViewportChange={setViewport}
              >
                {/* Draw component for displaying sale boundaries */}
                <Draw
                  data={boundaryFeatureCollection}
                  mode="static"
                  modes={(defaultModes: { [x: string]: any }) => ({
                    ...defaultModes,
                    static: StaticMode,
                  })}
                  styles={mapVisualizationDrawStyles}
                  lineStringControl={false}
                  polygonControl={false}
                  pointControl={false}
                  combineFeaturesControl={false}
                  uncombineFeaturesControl={false}
                  trashControl={false}
                />
              </MapGL>
            </div>
          </div>

          {/* Sale Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Sale Summary
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Sale Type
                </label>
                <p className="text-gray-900 capitalize">{sale.sale_type}</p>
              </div>

              {sale.published_at && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Published
                  </label>
                  <p className="text-gray-900">
                    {sale.published_at.toLocaleDateString()}
                  </p>
                </div>
              )}

              {sale.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <p className="text-gray-900 text-sm leading-relaxed">
                    {sale.description}
                  </p>
                </div>
              )}

              {/* Bid count - only show for foresters */}
              {isForester && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Bids
                  </label>
                  <button
                    onClick={() =>
                      void navigate(`/birddog/sale/${saleId}/bids`)
                    }
                    className="text-blue-600 hover:text-blue-800 text-sm underline bg-transparent border-none cursor-pointer p-0"
                  >
                    {bidCount} {bidCount === 1 ? "bid" : "bids"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Your Bid Section - Only for timber buyers who have submitted a bid */}
        {isTimberBuyer && userBid && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Your Bid</h2>
              {biddingStatus === "open" && (
                <button
                  onClick={() =>
                    alert("Edit bid functionality doesn't exist yet")
                  }
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md transition-colors text-sm"
                >
                  Edit Bid
                </button>
              )}
            </div>

            {userBid.type === "lump sum" ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Lump Sum Bid
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Submitted {userBid.submitted_at.toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-green-700">
                    {formatCurrency(userBid.total_amount)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Pay-as-Cut Bid
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Submitted {userBid.submitted_at.toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-lg font-bold text-green-700">
                    Est. Total:{" "}
                    {formatCurrency(
                      userBid.product_prices.reduce((total, product) => {
                        const saleProduct = sale?.timber_products.find(
                          (p) => p.name === product.product_name
                        )
                        return (
                          total +
                          product.price_per_unit *
                            (saleProduct?.estimated_volume || 0)
                        )
                      }, 0)
                    )}
                  </p>
                </div>
                <div className="space-y-2">
                  {userBid.product_prices.map((product, index) => {
                    const saleProduct = sale?.timber_products.find(
                      (p) => p.name === product.product_name
                    )
                    const estimatedTotal =
                      product.price_per_unit *
                      (saleProduct?.estimated_volume || 0)
                    return (
                      <div
                        key={index}
                        className="flex justify-between items-center text-sm"
                      >
                        <span className="text-green-800 font-medium">
                          {product.product_name}
                        </span>
                        <div className="text-right">
                          <span className="text-green-700">
                            {formatCurrency(product.price_per_unit)}/unit
                          </span>
                          {saleProduct && (
                            <span className="block text-xs text-green-600">
                              Est: {formatCurrency(estimatedTotal)}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Inventory Details */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Inventory Details
          </h2>
          <div className="overflow-x-auto">
            <table className="table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Type
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estimated Volume
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sale.timber_products.map((product, index) => (
                  <tr key={index}>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.name}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      {product.estimated_volume.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {product.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Inventory Description */}
          {sale.inventory_description && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Additional Inventory Details
              </h3>
              <div className="prose prose-sm max-w-none text-gray-600">
                <p className="whitespace-pre-wrap">
                  {sale.inventory_description}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Bidding Terms and Conditions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Bidding Terms and Conditions
          </h2>
          <div className="prose prose-sm max-w-none text-gray-600">
            {sale.bidding_tc ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {sale.bidding_tc}
              </ReactMarkdown>
            ) : (
              <p>
                Bidding terms and conditions will be displayed here. This
                section will contain important information about how to
                participate in the bidding process, minimum bid requirements,
                and bidding deadlines.
              </p>
            )}
          </div>
        </div>

        {/* Contract Terms and Conditions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Contract Terms and Conditions
          </h2>
          <div className="prose prose-sm max-w-none text-gray-600">
            {sale.contract_tc ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {sale.contract_tc}
              </ReactMarkdown>
            ) : (
              <p>
                Contract terms and conditions will be displayed here. This
                section will outline the legal requirements, payment terms,
                harvesting guidelines, and other contractual obligations.
              </p>
            )}
          </div>
        </div>
      </div>
    </BirddogLayout>
  )
}
